import React, { useState, useEffect, useCallback } from 'react';
import { Box, Grid, Typography, Card, CardContent, MenuItem, Select, Chip, Stack, CircularProgress, Alert, Pagination } from "@mui/material";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { format, parseISO } from 'date-fns';
import { getData } from '../../services/axiosInstance';
import { API_ENDPOINTS } from '../../services/endpoints';

function MyLeaveRequests() {
    const today = new Date();
    const [year, setYear] = useState(today.getFullYear().toString());
    const [month, setMonth] = useState((today.getMonth() + 1).toString());
    const [page, setPage] = useState(1);
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;

    const fetchLeaveHistory = useCallback(async () => {
        if (!userId) {
            setError("User session not found. Please login again.");
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const url = API_ENDPOINTS.getEmployeeLeaveHistory(userId, year, month, page, 6);
            const response = await getData(url);

            if (response?.status === "SUCCESS" && response?.result) {
                setLeaveRequests(response.result.leaveHistory || []);
                setTotalPages(response.result.totalPages || 1);
            } else {
                setLeaveRequests([]);
            }
        } catch (err) {
            setError("Unable to fetch leave history.");
        } finally {
            setLoading(false);
        }
    }, [userId, year, month, page]);

    useEffect(() => {
        fetchLeaveHistory();
    }, [fetchLeaveHistory]);

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const months = [
        { value: "1", label: "January" }, { value: "2", label: "February" },
        { value: "3", label: "March" }, { value: "4", label: "April" },
        { value: "5", label: "May" }, { value: "6", label: "June" },
        { value: "7", label: "July" }, { value: "8", label: "August" },
        { value: "9", label: "September" }, { value: "10", label: "October" },
        { value: "11", label: "November" }, { value: "12", label: "December" }
    ];

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'APPROVED': return '#10b981';
            case 'PENDING': return '#f59e0b';
            case 'REJECTED': return '#ef4444';
            default: return '#6b7280';
        }
    };

    return (
        <Box>
            <Box sx={{
                backgroundColor: '#f4f4f4', p: 2, border: '1px solid #e0e0e0', borderRadius: 3, mb: 3,
                display: 'flex', gap: 4, boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
            }}>
                <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>YEAR</Typography>
                    <Select
                        value={year}
                        onChange={(e) => { setYear(e.target.value); setPage(1); }}
                        size="small"
                        variant="standard"
                        sx={{ fontWeight: 600, minWidth: 80, '&:before, &:after': { display: 'none' } }}
                    >
                        <MenuItem value="2024">2024</MenuItem>
                        <MenuItem value="2025">2025</MenuItem>
                        <MenuItem value="2026">2026</MenuItem>
                    </Select>
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>MONTH</Typography>
                    <Select
                        value={month}
                        onChange={(e) => { setMonth(e.target.value); setPage(1); }}
                        size="small"
                        variant="standard"
                        sx={{ fontWeight: 600, minWidth: 120, '&:before, &:after': { display: 'none' } }}
                    >
                        {months.map((m) => (
                            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                        ))}
                    </Select>
                </Box>
            </Box>

            {/* Content Section */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
            ) : error ? (
                <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
            ) : leaveRequests.length === 0 ? (
                <Typography sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                    No records found for {months.find(m => m.value === month)?.label} {year}.
                </Typography>
            ) : (
                <>
                    <Grid container spacing={3}>
                        {leaveRequests.map((request, index) => (
                            <Grid size={{ xs: 12, md: 6 }} key={index}>
                                <Card sx={{ borderRadius: 4, backgroundColor: '#f4f4f4', border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                                    <CardContent sx={{ p: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                            <Chip
                                                label={request.leave_type}
                                                sx={{ backgroundColor: '#eef2ff', color: '#6366f1', fontWeight: 600, borderRadius: 2 }}
                                            />
                                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: getStatusColor(request.status) }}>
                                                <CheckCircleOutlineIcon fontSize="small" />
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {request.status}
                                                </Typography>
                                            </Stack>
                                        </Box>

                                        <Stack spacing={2.5}>
                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                <CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5 }} />
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>DATE RANGE</Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                                        {format(parseISO(request.start_date), 'dd/MM/yyyy')}
                                                        {request.start_date !== request.end_date && ` - ${format(parseISO(request.end_date), 'dd/MM/yyyy')}`}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        {format(parseISO(request.start_date), 'EEEE')}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5 }} />
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>REASON</Typography>
                                                    <Typography variant="body2" sx={{ color: 'text.primary', mt: 0.5 }}>{request.reason}</Typography>
                                                </Box>
                                            </Box>

                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                <ChatBubbleOutlineIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5 }} />
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>COMMENTS</Typography>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 0.5 }}>
                                                        {request.comment || "No comments provided"}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}

export default MyLeaveRequests;